// The A7 in the iPad mini 3 may not keep up with -webkit-backdrop-filter on 7 cards. On first
// launch per device we sample frame times; if they're poor, "Reduce effects" is switched on.
// The numbers are reported to the API log so ADR-0001's spike table can be filled in.

export interface FrameStats {
  frames: number;
  medianMs: number;
  p90Ms: number;
  slow: boolean;
}

export const SLOW_MEDIAN_MS = 22; // under ~45 fps
export const SLOW_P90_MS = 40;

export function summarizeFrames(deltas: number[]): FrameStats {
  const d = deltas.filter((x) => x > 0 && x < 1000).sort((a, b) => a - b);
  if (d.length < 10) return { frames: d.length, medianMs: 0, p90Ms: 0, slow: false };
  const at = (q: number) => d[Math.min(d.length - 1, Math.floor(q * d.length))]!;
  const medianMs = Math.round(at(0.5) * 10) / 10;
  const p90Ms = Math.round(at(0.9) * 10) / 10;
  return { frames: d.length, medianMs, p90Ms, slow: medianMs > SLOW_MEDIAN_MS || p90Ms > SLOW_P90_MS };
}

/** Samples requestAnimationFrame deltas for `ms` after a settle delay. */
export function sampleFrames(ms: number, settleMs: number): Promise<number[]> {
  return new Promise((resolve) => {
    setTimeout(() => {
      const deltas: number[] = [];
      let last = 0;
      let start = 0;
      const step = (t: number) => {
        if (!start) start = t;
        if (last) deltas.push(t - last);
        last = t;
        if (t - start < ms) requestAnimationFrame(step);
        else resolve(deltas);
      };
      requestAnimationFrame(step);
    }, settleMs);
  });
}

export const PROBE_KEY = 'obv-perf-probe-v1';

export function probeDone(): boolean {
  try {
    return window.localStorage.getItem(PROBE_KEY) !== null;
  } catch {
    return true;
  }
}

export function markProbeDone(stats: FrameStats): void {
  try {
    window.localStorage.setItem(PROBE_KEY, JSON.stringify(stats));
  } catch {
    // Storage unavailable (private mode): the probe simply runs again next launch.
  }
}
